import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Grid, Box, Card as MuiCard, CardContent as MuiCardContent, Typography, Divider, IconButton, Tooltip } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import KeyIcon from "@mui/icons-material/Key";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { spacing } from "@mui/system";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import apiUtils from "../../utils/apiUtils";
import ViewPortalLoginDialog from "../../components/ViewPortalLoginDialog";
import ViewSupplierPortalLoginDialog from "../../components/ViewSupplierPortalLoginDialog";
import ViewEuVatPortalLoginDialog from "../../components/ViewEuVatPortalLoginsDialog";
import ViewOtherPortalLoginDialog from "../../components/ViewOtherPortalLoginDialog";
import AddCustomerPortalLogin from "../../components/AddCustomerPortalLogin";
import AddSupplierPortalLogin from "../../components/AddSupplierPortalLogin";
import AddEuVatPortalLogin from "../../components/AddEuVatPortalLogin";
import AddOtherPortalLogin from "../../components/AddOtherPortalLogin";
import { useCallback } from "react";
import DataGridExport from "../../components/DataGridExport";
// import moment from "moment";
// import { de } from "date-fns/locale";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const actionsBoxSx = {
  display: "flex",
  alignItems: "center",
  gap: 0,
  width: "fit-content",
};

const compactActionButtonSx = {
  p: "2px",
};

const CopyOtherInformationAction = ({ email, onCopy }) => (
  <Tooltip title="Copy Other Information">
    <span>
      <IconButton
        size="small"
        sx={compactActionButtonSx}
        aria-label="Copy Other Information"
        onClick={() => onCopy(email, "Other information")}
        disabled={!email}
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
);

const CopyLoginActions = ({ username, customerLoginId, onCopy, onCopyPassword }) => (
  <>
    <Tooltip title="Copy Username">
      <span>
        <IconButton
          size="small"
          sx={compactActionButtonSx}
          aria-label="Copy Username"
          onClick={() => onCopy(username, "Username")}
          disabled={!username}
        >
          <PersonIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
    <Tooltip title="Copy Password">
      <span>
        <IconButton
          size="small"
          sx={compactActionButtonSx}
          aria-label="Copy Password"
          onClick={() => onCopyPassword(customerLoginId)}
          disabled={!customerLoginId}
        >
          <KeyIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </>
);

const CustomerLogins = ({ customerId }) => {
  const supplierPortalData = apiUtils.useApiData("/api/v1/supplierPortalInfo");

  const columnsPortalLogins = [
    {
      field: "id",
      headerName: "Portal Login ID",
      width: 50,
      hide: true,
    },
    { field: "firstname", headerName: "Firstname", flex: 0.5 },
    { field: "lastname", headerName: "Lastname", flex: 0.5 },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      renderCell: (params) => {
        return (
          <Box sx={actionsBoxSx}>
            <CopyOtherInformationAction email={params.row.email} onCopy={handleCopyToClipboard} />
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={(e) => {
                setViewPortalLoginDialog({
                  isOpen: true,
                  title: "Edit portal login",
                  params: params.row,
                  customerId: customerId,
                  test: [],
                  subTitle: "Edit " + params.row.iban + "?",
                  onConfirm: () => {
                    onEditConfirmed(e, params.row);
                  },
                });
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={(e) => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Portal login",
                  subTitle:
                    "Portal Login will also be removed from Azure Entra ID. Are you sure you want to delete this Portal Login?",
                  onConfirm: () => {
                    onDeletePortalLoginConfirmed(e, params.row);
                  },
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const columnsSupplierPortalLogins = [
    {
      field: "customerLoginId",
      headerName: "Portal Login ID",
      width: 50,
      hide: true,
    },
    {
      field: "supplierPortalId",
      headerName: "Supplier Name",
      flex: 1,
      valueGetter: (params) => {
        const supplier = supplierPortalData.find((s) => s.supplierPortalId === params.row.supplierPortalId);
        return supplier ? supplier.name : "";
      },
    },
    { field: "username", headerName: "Username", flex: 0.5 },
    {
      field: "actions",
      headerName: "Actions",
      width: 152,
      minWidth: 152,
      maxWidth: 152,
      renderCell: (params) => {
        return (
          <Box sx={actionsBoxSx}>
            <CopyLoginActions
              username={params.row.username}
              customerLoginId={params.row.customerLoginId}
              onCopy={handleCopyToClipboard}
              onCopyPassword={handleCopyPassword}
            />
            <CopyOtherInformationAction email={params.row.email} onCopy={handleCopyToClipboard} />
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={() => {
                const supplier = supplierPortalData.find((s) => s.supplierPortalId === params.row.supplierPortalId);
                if (supplier && supplier.url) {
                  window.open(supplier.url, "_blank");
                }
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={() => {
                openStoredLoginDialog(params.row, "Edit Portal Login", setViewSupplierPortalLoginDialog);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={(e) => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Portal Login",
                  subTitle: "Are you sure you want to delete this login?",
                  onConfirm: () => onDeleteLoginConfirmed(e, params.row),
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const columnsEuVatPortalLogins = [
    {
      field: "customerLoginId",
      headerName: "Portal Login ID",
      width: 50,
      hide: true,
    },
    // {
    //   field: "supplierPortalId",
    //   headerName: "Supplier Name",
    //   flex: 1,
    //   valueGetter: (params) => {
    //     const supplier = supplierPortalData.find((s) => s.supplierPortalId === params.row.supplierPortalId);
    //     return supplier ? supplier.name : "";
    //   },
    // },
    { field: "username", headerName: "Username", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 128,
      minWidth: 128,
      maxWidth: 128,
      renderCell: (params) => {
        return (
          <Box sx={actionsBoxSx}>
            <CopyLoginActions
              username={params.row.username}
              customerLoginId={params.row.customerLoginId}
              onCopy={handleCopyToClipboard}
              onCopyPassword={handleCopyPassword}
            />
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={() => window.open("https://eubtw.belastingdienst.nl/netp/", "_blank")}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={() => {
                openStoredLoginDialog(params.row, "Edit EU VAT Portal Login", setViewEuVatPortalLoginDialog);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={(e) => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Portal Login",
                  subTitle: "Are you sure you want to delete this login?",
                  onConfirm: () => onDeleteLoginConfirmed(e, params.row),
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const columnsOtherPortalLogins = [
    {
      field: "customerLoginId",
      headerName: "Portal Login ID",
      width: 50,
      hide: true,
    },
    // {
    //   field: "supplierPortalId",
    //   headerName: "Supplier Name",
    //   flex: 1,
    //   valueGetter: (params) => {
    //     const supplier = supplierPortalData.find((s) => s.supplierPortalId === params.row.supplierPortalId);
    //     return supplier ? supplier.name : "";
    //   },
    // },
    { field: "username", headerName: "Username", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 128,
      minWidth: 128,
      maxWidth: 128,
      renderCell: (params) => {
        return (
          <Box sx={actionsBoxSx}>
            <CopyLoginActions
              username={params.row.username}
              customerLoginId={params.row.customerLoginId}
              onCopy={handleCopyToClipboard}
              onCopyPassword={handleCopyPassword}
            />
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={() => window.open(params.row.url, "_blank")}
              disabled={!params.row.url || params.row.url.trim() === ""}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={() => {
                openStoredLoginDialog(params.row, "Edit Other Portal Login", setViewOtherPortalLoginDialog);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={compactActionButtonSx}
              onClick={(e) => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Portal Login",
                  subTitle: "Are you sure you want to delete this login?",
                  onConfirm: () => onDeleteLoginConfirmed(e, params.row),
                });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const [tablePortalLogins, setTablePortalLogins] = useState([]);
  const [loadingPortalLogins, setLoadingPortalLogins] = useState(false);

  const [supplierPortalLogins, setSupplierPortalLogins] = useState([]);
  const [euVatPortalLogins, setEuVatPortalLogins] = useState([]);
  const [otherPortalLogins, setOtherPortalLogins] = useState([]);
  const [loading, setLoading] = useState(false);

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const loadStoredLogin = async (customerLoginId) => {
    return apiUtils.fetchData(`/api/v1/customerLogins/${customerLoginId}`);
  };

  const handleCopyPassword = async (customerLoginId) => {
    try {
      const login = await loadStoredLogin(customerLoginId);
      await handleCopyToClipboard(login?.password, "Password");
    } catch (error) {
      console.error("Failed to load portal password:", error);
      setNotify({ isOpen: true, message: "Failed to copy password", type: "error" });
    }
  };

  const openStoredLoginDialog = async (row, title, setDialog) => {
    try {
      const login = await loadStoredLogin(row.customerLoginId);
      setDialog({
        isOpen: true,
        title,
        params: { ...row, password: login?.password ?? "" },
        customerId,
      });
    } catch (error) {
      console.error("Failed to load portal login:", error);
      setNotify({ isOpen: true, message: "Failed to open portal login", type: "error" });
    }
  };

  const handleCopyToClipboard = async (text, label) => {
    if (!text) {
      setNotify({ isOpen: true, message: `No ${label.toLowerCase()} to copy`, type: "warning" });
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setNotify({ isOpen: true, message: `${label} copied to clipboard`, type: "success" });
    } catch (error) {
      console.error("Failed to copy:", error);
      setNotify({ isOpen: true, message: `Failed to copy ${label.toLowerCase()}`, type: "error" });
    }
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [viewPortalLoginDialog, setViewPortalLoginDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    test: [],
  });

  const [viewSupplierPortalLoginDialog, setViewSupplierPortalLoginDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    test: [],
  });

  const [viewEuVatPortalLoginDialog, setViewEuVatPortalLoginDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    test: [],
  });

  const [viewOtherPortalLoginDialog, setViewOtherPortalLoginDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    test: [],
  });

  const fetchPortalLogins = useCallback(async () => {
    try {
      setLoadingPortalLogins(true);
      const data = await apiUtils.fetchData(`/api/v1/AppUsersByEntityId/${customerId}`);
      console.log(data);
      setTablePortalLogins(data);
      setLoadingPortalLogins(false);
    } catch (error) {
      console.error(error);
      setLoadingPortalLogins(false);
      // Handle error here...
    }
  }, [customerId]);

  const fetchSupplierPortalLogins = useCallback(async () => {
    try {
      setLoadingPortalLogins(true);
      const data = await apiUtils.fetchData(`/api/v1/customerLoginsByEntityId/${customerId}`);
      // Filter to include only records where customerLoginTypeId is 1 (SupplierPortal)
      const supplierPortalLoginfilteredData = data.filter((login) => login.customerLoginTypeId === 1);
      const euVatPortalLoginfilteredData = data.filter((login) => login.customerLoginTypeId === 2);
      const otherPortalLoginfilteredData = data.filter((login) => login.customerLoginTypeId === 4);

      setSupplierPortalLogins(supplierPortalLoginfilteredData);
      setEuVatPortalLogins(euVatPortalLoginfilteredData);
      setOtherPortalLogins(otherPortalLoginfilteredData);
      setLoadingPortalLogins(false);
    } catch (error) {
      console.error(error);
      setLoadingPortalLogins(false);
    }
  }, [customerId]);

  const onDeleteLoginConfirmed = async (e, row) => {
    try {
      // Verwijder het record via de API
      const result = await apiUtils.deleteData(`/api/v1/customerLogins/${row.customerLoginId}`);
      if (result) {
        setNotify({ isOpen: true, message: "Deleted Successfully", type: "success" });
      }
      fetchSupplierPortalLogins();
    } catch (error) {
      console.error(error);
      setNotify({ isOpen: true, message: "Error deleting login", type: "error" });
    }
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  useEffect(() => {
    fetchPortalLogins();
  }, [fetchPortalLogins]);

  useEffect(() => {
    fetchSupplierPortalLogins();
  }, [fetchSupplierPortalLogins]);

  if (loadingPortalLogins) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  const onDeletePortalLoginConfirmed = async (e, params) => {
    try {
      const deleteResult = await apiUtils.deleteData(`/api/v1/DeleteUser/${params.id}`);
      console.log(deleteResult);
      if (deleteResult) {
        await apiUtils.deleteData(`/api/v1/PortalLogins/${params.portalLoginId}`);
        setNotify({
          isOpen: true,
          message: "Deleted Successfully",
          type: "success",
        });
      }
      fetchPortalLogins();
    } catch (error) {
      console.error(error);
      // handle error here...
    }

    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const onEditConfirmed = (e, params) => {
    setViewPortalLoginDialog({ ...viewPortalLoginDialog, isOpen: false });
  };

  return (
    <React.Fragment>
      <Helmet title="Customer Financials" />
      <Divider my={6} />
      <Grid item xs={12} md={6}>
        {/* <FormDialog /> */}
      </Grid>
      <Card mb={0}>
        <CardContent pb={1} sx={{ marginBottom: 0 }}>
          {/* <Typography variant="h6" gutterBottom>
            Customer Logins
          </Typography> */}
        </CardContent>
        <Grid container spacing={0} mt={0}>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
          <ViewPortalLoginDialog
            viewPortalLoginDialog={viewPortalLoginDialog}
            setViewPortalLoginDialog={setViewPortalLoginDialog}
            onPortalLoginUpdate={fetchPortalLogins}
          />
          <ViewSupplierPortalLoginDialog
            viewSupplierPortalLoginDialog={viewSupplierPortalLoginDialog}
            setViewSupplierPortalLoginDialog={setViewSupplierPortalLoginDialog}
            onSupplierPortalLoginUpdate={fetchSupplierPortalLogins}
          />
          <ViewEuVatPortalLoginDialog
            viewEuVatPortalLoginDialog={viewEuVatPortalLoginDialog}
            setViewEuVatPortalLoginDialog={setViewEuVatPortalLoginDialog}
            onEuVatPortalLoginUpdate={fetchSupplierPortalLogins}
          />
          <ViewOtherPortalLoginDialog
            viewOtherPortalLoginDialog={viewOtherPortalLoginDialog}
            setViewOtherPortalLoginDialog={setViewOtherPortalLoginDialog}
            onOtherPortalLoginUpdate={fetchSupplierPortalLogins}
          />
          <Grid xs={5}>
            <Typography variant="h6" gutterBottom>
              Supplier Portal Logins
            </Typography>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <AddSupplierPortalLogin onDataRefresh={fetchSupplierPortalLogins} customerId={customerId} />
              </Grid>
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <DataGridExport tableData={supplierPortalLogins} columns={columnsSupplierPortalLogins} />
                </div>
              </Grid>
            </Grid>
            <DataGrid
              getRowId={(row) => row.customerLoginId}
              disableColumnFilter
              disableSelectionOnClick
              disableColumnSelector
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              rows={supplierPortalLogins}
              columns={columnsSupplierPortalLogins}
              pageSize={10}
              sx={{
                height: 307,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                marginTop: 0,
              }}
            />
          </Grid>
          <Grid xs={2}></Grid>
          <Grid xs={5}>
            <Typography variant="h6" gutterBottom>
              Customer Portal Logins
            </Typography>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <AddCustomerPortalLogin onDataRefresh={fetchPortalLogins} customerId={customerId} />
              </Grid>
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <DataGridExport tableData={tablePortalLogins} columns={columnsPortalLogins} />
                </div>
              </Grid>
            </Grid>
            <DataGrid
              getRowId={(row) => row.id}
              disableColumnFilter
              disableSelectionOnClick
              disableColumnSelector
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              rows={tablePortalLogins}
              columns={columnsPortalLogins}
              pageSize={10}
              sx={{
                height: 307,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                marginTop: 0,
              }}
            />
          </Grid>
          <Grid xs={5} style={{ marginTop: 20 }}>
            <Typography variant="h6" gutterBottom>
              EU VAT Logins
            </Typography>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <AddEuVatPortalLogin onDataRefresh={fetchSupplierPortalLogins} customerId={customerId} />
              </Grid>
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <DataGridExport tableData={euVatPortalLogins} columns={columnsEuVatPortalLogins} />
                </div>
              </Grid>
            </Grid>
            <DataGrid
              getRowId={(row) => row.customerLoginId}
              disableColumnFilter
              disableSelectionOnClick
              disableColumnSelector
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              rows={euVatPortalLogins}
              columns={columnsEuVatPortalLogins}
              pageSize={10}
              sx={{
                height: 307,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                marginTop: 0,
              }}
            />
          </Grid>
          <Grid xs={2} style={{ marginTop: 20 }}></Grid>
          <Grid xs={5} style={{ marginTop: 20 }}>
            <Typography variant="h6" gutterBottom>
              Other Logins
            </Typography>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <AddOtherPortalLogin onDataRefresh={fetchSupplierPortalLogins} customerId={customerId} />
              </Grid>
              <Grid item xs={6} style={{ margin: 0, padding: 0 }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <DataGridExport tableData={otherPortalLogins} columns={columnsOtherPortalLogins} />
                </div>
              </Grid>
            </Grid>
            <DataGrid
              getRowId={(row) => row.customerLoginId}
              disableColumnFilter
              disableSelectionOnClick
              disableColumnSelector
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              rows={otherPortalLogins}
              columns={columnsOtherPortalLogins}
              pageSize={10}
              sx={{
                height: 307,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                marginTop: 0,
              }}
            />
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default CustomerLogins;
